/* eslint-disable @typescript-eslint/no-var-requires */
import { Document, Font, Image, PDFViewer, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import globoLogo from 'assets/icons/globo-logo.png';

import { ITEM_STATUS } from 'commons/constants';
import { addLeadingZeros, dateFormatter, formatCurrency } from 'commons/utils';
import { IOrderCover } from 'services/orders';

import { RenderPDF } from 'components/elements';
import { getColumns } from './constants';
import { Styled } from './styles';

const fontRegular = require('assets/fonts/inter/inter-400-normal.woff');
const fontBold = require('assets/fonts/inter/inter-700-normal.woff');

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: fontRegular,
      fontWeight: 'normal',
    },
    {
      src: fontBold,
      fontStyle: 'normal',
      fontWeight: 'bold',
    },
  ],
});

const styles = StyleSheet.create(Styled as any);

type OrderCoverPDFType = {
  open: boolean;
  close: () => void;
  order: IOrderCover;
  isScreenAgency?: boolean;
};

export const OrderCoverPDF = ({ open, close, order, isScreenAgency = false }: OrderCoverPDFType) => {
  const columns = getColumns(isScreenAgency);

  return (
    <RenderPDF open={open} close={close}>
      <PDFViewer style={styles.pdf}>
        <Document>
          <Page size="A4" style={styles.body}>
            <View style={styles.header} fixed>
              <View>
                <Text style={styles.textBold}>Pedido de Figuração nº: {addLeadingZeros(order.id, 6)}</Text>
              </View>

              <View style={styles.alignRight}>
                <Image src={globoLogo} style={styles.logo} />
              </View>
            </View>

            <View style={styles.subHeader}>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Solicitante: </Text>
                <Text style={styles.text}>{order?.userLoggedName ?? order.userLoggedEmail ?? '--'}</Text>
              </View>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Conteúdo: </Text>
                <Text style={styles.text}>{order.content ?? '--'}</Text>
              </View>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Roteiro: </Text>
                <Text style={styles.text}>{order.script ?? '--'}</Text>
              </View>
              {isScreenAgency && (
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Agência: </Text>
                  <Text style={styles.text}>{order.items[0]?.agency?.name ?? '--'}</Text>
                </View>
              )}
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Tipo de gravação: </Text>
                <Text style={styles.text}>{order.recordingType ?? '--'}</Text>
              </View>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Situação: </Text>
                <Text style={styles.text}>{ITEM_STATUS[order.status] ?? '--'}</Text>
              </View>
            </View>

            <View style={styles.separator()} />

            <View style={styles.section}>
              <View style={styles.sectionLeft}>
                <Text style={styles.textBold}>Apresentação</Text>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Data: </Text>
                  <Text style={styles.text}>
                    {order.backGroundActorPresentationDate
                      ? dateFormatter(order.backGroundActorPresentationDate)
                      : '--'}
                  </Text>
                </View>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Local: </Text>
                  <Text style={styles.text}>{order.backGroundActorPresentationPlace ?? '--'}</Text>
                </View>
              </View>

              <View style={styles.sectionRight}>
                <Text style={styles.textBold}>Gravação</Text>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Data: </Text>
                  <Text style={styles.text}>
                    {order.startRecordingDate ? dateFormatter(order.startRecordingDate) : '--'}
                  </Text>
                </View>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Local: </Text>
                  <Text style={styles.text}>{order.recordingPlace ?? '--'}</Text>
                </View>
              </View>
            </View>

            <View style={styles.separator()} />

            <Text style={styles.textBold}>
              Total de pessoas na figuração: <Text style={styles.text}>{order.expectedQuantity ?? '--'}</Text>
            </Text>

            <View style={styles.section}>
              <View style={styles.sectionLeft}>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Adultos: </Text>
                  <Text style={styles.text}>{order.expectedBackgroundActor ?? '--'}</Text>
                </View>

                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Menores: </Text>
                  <Text style={styles.text}>{order.expectedBackgroundActorMinor ?? '--'}</Text>
                </View>

                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Veículos: </Text>
                  <Text style={styles.text}>{order.expectedBackgroundActorVehicle ?? '--'}</Text>
                </View>
              </View>

              <View style={styles.sectionRight}>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Animais: </Text>
                  <Text style={styles.text}>{order.expectedBackgroundActorPet ?? '--'}</Text>
                </View>

                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Acompanhantes: </Text>
                  <Text style={styles.text}>{order.expectedCompanions ?? '--'}</Text>
                </View>
                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>-</Text>
                </View>

                <View style={styles.flexRow}>
                  <Text style={styles.textBold}>Fiscais: </Text>
                  <Text style={styles.text}>{order.expectedFiscal ?? '--'}</Text>
                </View>
              </View>
            </View>

            <View style={styles.section}>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Observações: </Text>
                <Text style={styles.text}>{order.recordingNotes ?? '--'}</Text>
              </View>
            </View>

            <View style={styles.separator()} />

            <View style={styles.subHeader}>
              <View style={styles.flexRow}>
                <Text style={styles.textBold}>Total Previsto: </Text>
                <Text style={styles.text}>{formatCurrency(order.totalCosts) ?? '--'}</Text>
              </View>
            </View>

            <View style={styles.separator()} />

            <View style={styles.allListFigurants}>
              <View style={styles.listFigurants}>
                <View style={styles.tableHeader}>
                  {columns?.map(({ name, width }, index) => (
                    <Text
                      key={`${name}-${index}`}
                      style={styles.tableColumn({
                        width,
                        hasBorder: false,
                        isBold: true,
                      })}
                    >
                      {name}
                    </Text>
                  ))}
                </View>

                <View>
                  {order?.items.map(item => (
                    <View style={styles.tableRow} key={item?.id}>
                      {columns?.map(({ keyValue, formatValue, width }, index) => (
                        <Text
                          key={`${index}- ${keyValue}`}
                          style={styles.tableColumn({
                            width,
                            hasBorder: true,
                            isBold: false,
                          })}
                        >
                          {formatValue(item?.[keyValue as 'arrivedTime'])}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
              <View style={styles.separator()} />
            </View>
          </Page>
        </Document>
      </PDFViewer>
    </RenderPDF>
  );
};
